import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Alert,
  AlertTitle,
  Button,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Error as ErrorIcon,
  Pets as PetsIcon,
  ArrowBack,
  Search,
} from '@mui/icons-material';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';

interface BirthErrorData {
  title: string;
  resumo: string;
  url: string;
}

interface BirthErrorViewProps {
  data: BirthErrorData;
  onBack?: () => void;
  onSearchMother?: () => void;
}

export const BirthErrorView: React.FC<BirthErrorViewProps> = ({
  data,
  onBack,
  onSearchMother,
}) => {
  const { t } = useTranslation(['farm', 'common']);
  const theme = useTheme();

  return (
    <Box
      sx={{
        p: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.default,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 3,
          gap: 2,
        }}
      >
        {onBack && (
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={onBack}
            sx={{
              borderColor: DESIGN_TOKENS.colors.neutral[300],
              color: DESIGN_TOKENS.colors.neutral[600],
              '&:hover': {
                borderColor: DESIGN_TOKENS.colors.neutral[400],
                backgroundColor: alpha(DESIGN_TOKENS.colors.neutral[100], 0.5),
              },
            }}
          >
            {t('common:back')}
          </Button>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PetsIcon
            sx={{
              color: DESIGN_TOKENS.colors.primary[500],
              fontSize: 28,
            }}
          />
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: DESIGN_TOKENS.colors.neutral[800],
            }}
          >
            {t('farm:birth.title', 'Nascimento de Animal')}
          </Typography>
        </Box>
      </Box>

      {/* Main Content */}
      <Paper
        elevation={2}
        sx={{
          flex: 1,
          p: 4,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
        }}
      >
        {/* Error Alert */}
        <Alert
          severity="error"
          icon={<ErrorIcon />}
          sx={{
            mb: 4,
            borderRadius: 2,
            '& .MuiAlert-icon': {
              fontSize: 28,
            },
          }}
        >
          <AlertTitle
            sx={{
              fontWeight: 600,
              fontSize: '1.1rem',
              mb: 1,
            }}
          >
            {t('farm:birth.error.title', 'Erro no Registro de Nascimento')}
          </AlertTitle>
          <Typography variant="body1">
            {data.resumo || t('farm:birth.error.mother_not_found', 'Mãe não encontrada na fazenda')}
          </Typography>
        </Alert>

        <Divider sx={{ my: 3 }} />

        {/* Information Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: DESIGN_TOKENS.colors.neutral[800],
              mb: 2,
            }}
          >
            {t('farm:birth.error.what_happened', 'O que aconteceu?')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: DESIGN_TOKENS.colors.neutral[600],
              lineHeight: 1.6,
              mb: 3,
            }}
          >
            {t('farm:birth.error.explanation', 
              'Durante o processo de registro do nascimento, não foi possível localizar a mãe do animal na fazenda. Isso pode acontecer se:'
            )}
          </Typography>
          <Box
            component="ul"
            sx={{
              pl: 3,
              color: DESIGN_TOKENS.colors.neutral[600],
              '& li': {
                mb: 1,
                lineHeight: 1.5,
              },
            }}
          >
            <li>{t('farm:birth.error.reason1', 'A mãe não está cadastrada no sistema')}</li>
            <li>{t('farm:birth.error.reason2', 'A identificação da mãe está incorreta')}</li>
            <li>{t('farm:birth.error.reason3', 'A mãe foi removida ou transferida da fazenda')}</li>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            justifyContent: 'center',
            mt: 4,
          }}
        >
          {onSearchMother && (
            <Button
              variant="contained"
              startIcon={<Search />}
              onClick={onSearchMother}
              sx={{
                backgroundColor: DESIGN_TOKENS.colors.primary[500],
                color: 'white',
                px: 3,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: DESIGN_TOKENS.colors.primary[600],
                },
              }}
            >
              {t('farm:birth.error.search_mother', 'Buscar Mãe')}
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={() => window.location.reload()}
            sx={{
              borderColor: DESIGN_TOKENS.colors.neutral[300],
              color: DESIGN_TOKENS.colors.neutral[700],
              px: 3,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              '&:hover': {
                borderColor: DESIGN_TOKENS.colors.neutral[400],
                backgroundColor: alpha(DESIGN_TOKENS.colors.neutral[100], 0.5),
              },
            }}
          >
            {t('farm:birth.error.try_again', 'Tentar Novamente')}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default BirthErrorView;